import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  TabContent,
  TabPane,
  Form,
  FormGroup,
  Label,
  Input,
  Alert
} from 'reactstrap';
import PropTypes from 'prop-types';
import { useAuthStore } from '../../zustand-store/auth.store';
import {
  connectDentally,
  getDentallySites,
  getDentallyConnection,
  bindSitePractitioner,
  getDentallyPractitioners,
  getReasonsMapping,
  mapService,
  getCustomFields,
  updateCustomFields,
  getPaymentPlans,
  updatePaymentPlan
} from '../../axios/dentally/index.api';

const DentallyDetailModal = ({
  isOpen,
  toggle,
  title,
  icon,
  description,
  isIntegrated,
  dentallyToken,
  refreshStatus
}) => {
  const [activeTab, setActiveTab] = useState('1');
  const [accessToken, setAccessToken] = useState(dentallyToken || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const hasToken = !!dentallyToken;
  const canAccessTabs = hasToken || isIntegrated;

  const [dentallySites, setDentallySites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [existingSiteId, setExistingSiteId] = useState(null);
  const [existingSiteName, setExistingSiteName] = useState(null);
  const [sitesTotal, setSitesTotal] = useState(0);

  const canAccessPractitioners = !!existingSiteId;

  const [dentallyPractitioners, setDentallyPractitioners] = useState([]);
  const [selectedPractitionerId, setSelectedPractitionerId] = useState('');
  const [existingPractitionerId, setExistingPractitionerId] = useState(null);
  const [existingPractitionerName, setExistingPractitionerName] = useState(null);
  const [practitionersTotal, setPractitionersTotal] = useState(0);

  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingPractitioners, setLoadingPractitioners] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [dentallyLocationId, setDentallyLocationId] = useState(null);

  const [slotServices, setSlotServices] = useState([]);
  const [dentallyServices, setDentallyServices] = useState([]);
  const [serviceMappings, setServiceMappings] = useState({});
  const [serviceLinkIds, setServiceLinkIds] = useState({});

  const [customFields, setCustomFields] = useState([]);
  const [customFieldMappings, setCustomFieldMappings] = useState({});
  const [loadingCustomFields, setLoadingCustomFields] = useState(false);
  const [initialCustomFields, setInitialCustomFields] = useState([]);

  const [paymentPlans, setPaymentPlans] = useState([]);
  const [selectedPaymentPlanId, setSelectedPaymentPlanId] = useState(null);
  const [selectedPaymentPlanName, setSelectedPaymentPlanName] = useState(null);
  const [existingPaymentPlanId, setExistingPaymentPlanId] = useState(null);
  const [loadingPaymentPlans, setLoadingPaymentPlans] = useState(false);

  const locationId = useAuthStore((state) => state.locationId);

  const isSiteMapped = !!existingSiteId;
  const isPractitionerMapped = !!existingPractitionerId;
  const isServicesUnlocked = isSiteMapped && isPractitionerMapped;

  useEffect(() => {
    setAccessToken(dentallyToken || '');
    setSuccess('');
    setSelectedSiteId('');
    setExistingSiteId(null);
    setExistingSiteName(null);
    setSelectedPractitionerId('');
    setExistingPractitionerId(null);
    setExistingPractitionerName(null);
    setDentallyLocationId(null);
    setActiveTab('1');
    setServiceMappings({});
    setServiceLinkIds({});
    setCustomFields([]);
    setCustomFieldMappings({});
    setInitialCustomFields([]);
    setPaymentPlans([]);
    setSelectedPaymentPlanId(null);
    setSelectedPaymentPlanName(null);
    setExistingPaymentPlanId(null);
  }, [dentallyToken, locationId]);

  useEffect(() => {
    if (isOpen && isIntegrated) {
      fetchExistingConnection();
    }
  }, [isOpen, isIntegrated]);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const handleSaveToken = async () => {
    if (!accessToken.trim()) {
      setError('Please enter an access token');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await connectDentally({ token: accessToken }, locationId);
      if (res?.status === 200 || res?.data?.code === 0) {
        setSuccess('Access token saved successfully!');
        if (refreshStatus) {
          refreshStatus();
        }
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || 'Failed to connect to Dentally. Please check your token.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingConnection = async () => {
    try {
      const connRes = await getDentallyConnection(locationId);
      if (connRes?.data?.code === 0 && connRes?.data?.data) {
        const connData = connRes.data.data;
        setDentallyLocationId(connData.id);
        setExistingSiteId(connData.dentallySiteId);
        setExistingSiteName(connData.dentallySiteName);
        if (connData.dentallySiteId) {
          setSelectedSiteId(connData.dentallySiteId);
        }
        setExistingPractitionerId(connData.dentallyPractionerId);
        setExistingPractitionerName(connData.dentallyPractionerName);
        if (connData.dentallyPractionerId) {
          setSelectedPractitionerId(connData.dentallyPractionerId.toString());
        }
        if (connData.paymentPlanId) {
          setExistingPaymentPlanId(connData.paymentPlanId);
          setSelectedPaymentPlanId(connData.paymentPlanId);
          setSelectedPaymentPlanName(connData.paymentPlanName);
        }
      }
    } catch (err) {
      console.error('Failed to fetch existing connection:', err);
    }
  };

  const toggleTab = async (tab) => {
    if (activeTab !== tab) {
      setTabLoading(true);
      if (tab === '1') {
        setAccessToken(dentallyToken || '');
      }
      if (tab === '2') {
        await fetchExistingConnection();
        await fetchAllSites();
      }
      if (tab === '3') {
        await fetchExistingConnection();
        await fetchAllPractitioners();
      }
      if (tab === '4') {
        setLoadingServices(true);
        try {
          const mappingRes = await getReasonsMapping(locationId);
          if (mappingRes?.data?.code === 0 && mappingRes?.data?.data) {
            const { slotServices: services, dentallyServices: reasons } = mappingRes.data.data;
            setSlotServices(services);
            setDentallyServices(reasons);
            const existingMappings = {};
            const existingLinkIds = {};
            services.forEach((service) => {
              if (service.dentallyServiceId) {
                existingMappings[service.id] = service.dentallyServiceId;
              }
              if (service.serviceLinkId) {
                existingLinkIds[service.id] = service.serviceLinkId;
              }
            });
            setServiceMappings(existingMappings);
            setServiceLinkIds(existingLinkIds);
          }
        } catch (err) {
          console.error('Failed to load services:', err);
        } finally {
          setLoadingServices(false);
        }
      }
      if (tab === '5') {
        setLoadingCustomFields(true);
        try {
          const customFieldsRes = await getCustomFields(locationId);
          if (customFieldsRes?.data?.data?.defaultCreatePatientArgs) {
            const fields = customFieldsRes?.data?.data?.defaultCreatePatientArgs;
            setCustomFields(fields);
            setInitialCustomFields(fields);
            const existingMappings = {};
            fields.forEach((field) => {
              if (field.selectedValue) {
                existingMappings[field.fieldName] = field.selectedValue;
              } else if (field.defaultValue) {
                existingMappings[field.fieldName] = field.defaultValue;
              }
            });
            setCustomFieldMappings(existingMappings);
          }
        } catch (err) {
          console.error('Failed to load custom fields:', err);
        } finally {
          setLoadingCustomFields(false);
        }
      }
      if (tab === '6') {
        setLoadingPaymentPlans(true);
        try {
          const paymentPlansRes = await getPaymentPlans(locationId);
          const plans = paymentPlansRes?.data?.data;
          if (plans?.bussinessPaymentPlans?.payment_plans) {
            setPaymentPlans(plans.bussinessPaymentPlans.payment_plans);
          }
        } catch (err) {
          console.error('Failed to load payment plans:', err);
        } finally {
          setLoadingPaymentPlans(false);
        }
      }
      setTabLoading(false);
      setActiveTab(tab);
    }
  };

  const fetchAllSites = async () => {
    setLoadingSites(true);
    try {
      const firstRes = await getDentallySites(locationId, 1);
      if (firstRes?.data?.code === 0 && firstRes?.data?.data) {
        const firstData = firstRes.data.data;
        const totalPages = firstData.meta?.totalPages ?? 1;
        let all = [...(firstData.sites ?? firstData)];
        for (let page = 2; page <= totalPages; page++) {
          const pageRes = await getDentallySites(locationId, page);
          if (pageRes?.data?.code === 0 && pageRes?.data?.data) {
            all = [...all, ...(pageRes.data.data.sites ?? pageRes.data.data)];
          }
        }
        setDentallySites(all);
        setSitesTotal(firstData.meta?.total ?? all.length);
      }
    } catch (err) {
      console.error('Failed to load sites:', err);
    } finally {
      setLoadingSites(false);
    }
  };

  const fetchAllPractitioners = async () => {
    setLoadingPractitioners(true);
    try {
      const firstRes = await getDentallyPractitioners(locationId, 1);
      if (firstRes?.data?.code === 0 && firstRes?.data?.data) {
        const firstData = firstRes.data.data;
        const totalPages = firstData.meta?.totalPages ?? 1;
        let all = [...(firstData.practioners ?? firstData)];
        for (let page = 2; page <= totalPages; page++) {
          const pageRes = await getDentallyPractitioners(locationId, page);
          if (pageRes?.data?.code === 0 && pageRes?.data?.data) {
            all = [...all, ...(pageRes.data.data.practioners ?? pageRes.data.data)];
          }
        }
        setDentallyPractitioners(all);
        setPractitionersTotal(firstData.meta?.total ?? all.length);
      }
    } catch (err) {
      console.error('Failed to load practitioners:', err);
    } finally {
      setLoadingPractitioners(false);
    }
  };

  const handleSaveSiteMapping = async () => {
    if (!selectedSiteId || !dentallyLocationId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const selectedSite = dentallySites.find((s) => s.id === selectedSiteId);
      const res = await bindSitePractitioner(dentallyLocationId, {
        dentallySiteId: selectedSiteId,
        dentallySiteName: selectedSite?.dentallySiteName || ''
      });
      if (res?.status === 200 || res?.data?.code === 0) {
        setExistingSiteId(selectedSiteId);
        setExistingSiteName(selectedSite?.dentallySiteName || '');
        setSuccess('Site mapping saved successfully!');
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || 'Failed to save site mapping.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePractitionerMapping = async () => {
    if (!selectedPractitionerId || !dentallyLocationId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const selectedPractitioner = dentallyPractitioners.find(
        (p) => p.id.toString() === selectedPractitionerId
      );
      const res = await bindSitePractitioner(dentallyLocationId, {
        dentallyPractitionerId: parseInt(selectedPractitionerId),
        dentallyPractitionerName: selectedPractitioner?.practitionerName || ''
      });
      if (res?.status === 200 || res?.data?.code === 0) {
        setExistingPractitionerId(parseInt(selectedPractitionerId));
        setExistingPractitionerName(selectedPractitioner?.practitionerName || '');
        setSuccess('Practitioner mapping saved successfully!');
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || 'Failed to save practitioner mapping.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceMappingChange = (slotServiceId, dentallyServiceId) => {
    setServiceMappings((prev) => ({
      ...prev,
      [slotServiceId]: dentallyServiceId
    }));
    if (!dentallyServiceId) {
      setServiceLinkIds((prev) => ({
        ...prev,
        [slotServiceId]: null
      }));
    }
  };

  const handleSaveServiceMappings = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const mappings = Object.entries(serviceMappings)
        .filter(([_, dentallyServiceId]) => dentallyServiceId)
        .map(([slotServiceId, dentallyServiceId]) => ({
          serviceId: parseInt(slotServiceId),
          serviceLinkId: serviceLinkIds[slotServiceId] || null,
          dentallyServiceId,
          dentallyServiceName:
            dentallyServices.find((s) => s.id === dentallyServiceId)?.reason || ''
        }));
      const res = await mapService({ services: mappings }, locationId);
      if (res?.status === 200 || res?.data?.code === 0) {
        setSuccess('Service mappings saved successfully!');
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || 'Failed to save service mappings.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomFieldChange = (fieldName, value) => {
    setCustomFieldMappings((prev) => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSaveCustomFields = async () => {
    if (!dentallyLocationId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const payload = customFields.map((field) => ({
        label: field.label,
        options: field.options || [],
        dataType: field.dataType,
        required: field.required,
        fieldName: field.fieldName,
        defaultValue: field.defaultValue || '',
        selectedValue: customFieldMappings[field.fieldName] || ''
      }));
      const res = await updateCustomFields(dentallyLocationId, payload);
      if (res?.status === 200 || res?.data?.code === 0) {
        setSuccess('Custom fields saved successfully!');
        const updatedFields = customFields.map((field) => ({
          ...field,
          selectedValue: customFieldMappings[field.fieldName] || ''
        }));
        setCustomFields(updatedFields);
        setInitialCustomFields(updatedFields);
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || 'Failed to save custom fields.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentPlanChange = (plan) => {
    setSelectedPaymentPlanId(plan.id);
    setSelectedPaymentPlanName(plan.name);
  };

  const handleSavePaymentPlan = async () => {
    if (!selectedPaymentPlanId || !dentallyLocationId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await updatePaymentPlan(
        dentallyLocationId,
        selectedPaymentPlanId,
        selectedPaymentPlanName
      );
      if (res?.status === 200 || res?.data?.code === 0) {
        setExistingPaymentPlanId(selectedPaymentPlanId);
        setSuccess('Payment plan saved successfully!');
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || 'Failed to save payment plan.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle} className="bg-light px-4 py-4">
        <div className="d-flex align-items-center mt-3">
          <img
            style={{
              width: '66px',
              height: '64px',
              marginRight: '20px',
              background: 'rgb(240,240,240)',
              borderRadius: '50%'
            }}
            src={icon}
            alt="icon"
            className="p-2"
          />
          <div>
            <h5 className="mb-0">{title}</h5>
            <small className="text-muted">{description}</small>
          </div>
        </div>
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        {success && <Alert color="success">{success}</Alert>}
        {tabLoading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading...</p>
          </div>
        )}
        {!tabLoading && (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3" style={{ gap: '8px' }}>
              {[
                { id: '1', label: 'Access Token', enabled: true },
                { id: '2', label: 'Map Location', enabled: canAccessTabs },
                { id: '3', label: 'Map Practitioner', enabled: canAccessPractitioners },
                { id: '4', label: 'Map Services', enabled: canAccessTabs },
                { id: '5', label: 'Customize Fields', enabled: canAccessTabs },
                { id: '6', label: 'Payment Plan', enabled: canAccessPractitioners }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => item.enabled && toggleTab(item.id)}
                  style={{
                    backgroundColor: item.id === activeTab ? '#000' : '#F2F2F2',
                    color: !item.enabled ? '#495057' : item.id === activeTab ? '#fff' : '#495057',
                    fontSize: '13px',
                    fontWeight: '500',
                    opacity: item.enabled ? 1 : 0.5,
                    padding: '6px 10px',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    paddingInline: 24,
                    paddingTop: 10,
                    paddingBottom: 10,
                    border: 'none',
                    cursor: item.enabled ? 'pointer' : 'not-allowed'
                  }}>
                  {item.label}
                </button>
              ))}
            </div>
            <TabContent activeTab={activeTab}>
              <TabPane tabId="1">
                <div className="p-3">
                  <Alert color="info">
                    <strong>Step 1:</strong> Log in to your Dentally account.
                    <br />
                    <strong>Step 2:</strong> Go to Settings → API Access.
                    <br />
                    <strong>Step 3:</strong> Generate a new API token.
                    <br />
                    <strong>Step 4:</strong> Copy the token and paste it below.
                  </Alert>

                  <Form>
                    <FormGroup>
                      <Label for="dentallyToken">Dentally Access Token</Label>
                      <Input
                        type="text"
                        id="dentallyToken"
                        placeholder="Enter your Dentally access token"
                        value={accessToken}
                        onChange={(e) => setAccessToken(e.target.value)}
                      />
                    </FormGroup>
                    <div className="d-flex justify-content-end">
                      <Button
                        color="primary"
                        onClick={handleSaveToken}
                        disabled={loading || accessToken == ''}>
                        {loading ? 'Saving...' : canAccessTabs ? 'Save' : 'Save'}
                      </Button>
                    </div>
                  </Form>
                </div>
              </TabPane>

              <TabPane tabId="2">
                <div className="p-3">
                  {!canAccessTabs ? (
                    <Alert color="warning">
                      Please connect to Dentally first using the Access Token tab.
                    </Alert>
                  ) : (
                    <div>
                      <h5>Step 2: Map Location</h5>
                      <p className="text-muted">
                        Select the Dentally site to map with this location.
                      </p>
                      {existingSiteId && (
                        <Alert color="success" className="mb-3">
                          Currently mapped to: <strong>{existingSiteName}</strong>
                        </Alert>
                      )}
                      {loadingSites ? (
                        <p>Loading sites...</p>
                      ) : (
                        <>
                          {sitesTotal > 0 && (
                            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                              {sitesTotal} site{sitesTotal !== 1 ? 's' : ''} available
                            </p>
                          )}
                          <div
                            className="mb-3"
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                              gap: '10px',
                              maxHeight: '340px',
                              overflowY: 'auto'
                            }}>
                            {dentallySites.map((site) => {
                              const isSelected = selectedSiteId === site.id;
                              return (
                                <div
                                  key={site.id}
                                  onClick={() => setSelectedSiteId(site.id)}
                                  style={{
                                    cursor: 'pointer',
                                    border: isSelected ? '2px solid #000' : '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    padding: '12px',
                                    background: isSelected ? '#f0f0f0' : '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}>
                                  <div
                                    style={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: '50%',
                                      background: isSelected ? '#000' : '#e9ecef',
                                      color: isSelected ? '#fff' : '#495057',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: 13,
                                      fontWeight: 600,
                                      flexShrink: 0
                                    }}>
                                    {site.dentallySiteName?.charAt(0)?.toUpperCase() || '?'}
                                  </div>
                                  <span
                                    style={{
                                      fontSize: 13,
                                      fontWeight: isSelected ? 600 : 400,
                                      wordBreak: 'break-word'
                                    }}>
                                    {site.dentallySiteName}
                                  </span>
                                  {isSelected && (
                                    <i
                                      className="feather icon-check-circle text-dark ml-auto"
                                      style={{ flexShrink: 0 }}></i>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <div className="d-flex justify-content-end">
                            <Button
                              color="primary"
                              disabled={!selectedSiteId || loading}
                              onClick={handleSaveSiteMapping}>
                              {loading
                                ? 'Saving...'
                                : existingSiteId
                                  ? 'Update Mapping'
                                  : 'Save Mapping'}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </TabPane>

              <TabPane tabId="3">
                <div className="p-3">
                  {!canAccessTabs ? (
                    <Alert color="warning">
                      Please connect to Dentally first using the Access Token tab.
                    </Alert>
                  ) : !canAccessPractitioners ? (
                    <Alert color="warning">
                      Please map a Site first before mapping a Practitioner.
                    </Alert>
                  ) : (
                    <div>
                      <h5>Step 3: Map Practitioner</h5>
                      <p className="text-muted">
                        Select the Dentally practitioner to map with this location.
                      </p>
                      {existingPractitionerId && (
                        <Alert color="success" className="mb-3">
                          Currently mapped to: <strong>{existingPractitionerName}</strong>
                        </Alert>
                      )}
                      {loadingPractitioners ? (
                        <p>Loading practitioners...</p>
                      ) : (
                        <>
                          {practitionersTotal > 0 && (
                            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                              {practitionersTotal} practitioner
                              {practitionersTotal !== 1 ? 's' : ''} available
                            </p>
                          )}
                          <div
                            className="mb-3"
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                              gap: '10px',
                              maxHeight: '340px',
                              overflowY: 'auto'
                            }}>
                            {dentallyPractitioners.map((prac) => {
                              const isSelected = selectedPractitionerId === prac.id.toString();
                              return (
                                <div
                                  key={prac.id}
                                  onClick={() => setSelectedPractitionerId(prac.id.toString())}
                                  style={{
                                    cursor: 'pointer',
                                    border: isSelected ? '2px solid #000' : '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    padding: '12px',
                                    background: isSelected ? '#f0f0f0' : '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}>
                                  <div
                                    style={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: '50%',
                                      background: isSelected ? '#000' : '#e9ecef',
                                      color: isSelected ? '#fff' : '#495057',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: 13,
                                      fontWeight: 600,
                                      flexShrink: 0
                                    }}>
                                    {prac.practitionerName?.charAt(0)?.toUpperCase() || '?'}
                                  </div>
                                  <span
                                    style={{
                                      fontSize: 13,
                                      fontWeight: isSelected ? 600 : 400,
                                      wordBreak: 'break-word'
                                    }}>
                                    {prac.practitionerName}
                                  </span>
                                  {isSelected && (
                                    <i
                                      className="feather icon-check-circle text-dark ml-auto"
                                      style={{ flexShrink: 0 }}></i>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <div className="d-flex justify-content-end">
                            <Button
                              color="primary"
                              disabled={!selectedPractitionerId || loading}
                              onClick={handleSavePractitionerMapping}>
                              {loading
                                ? 'Saving...'
                                : existingPractitionerId
                                  ? 'Update Mapping'
                                  : 'Save Mapping'}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </TabPane>

              <TabPane tabId="4">
                <div className="p-3">
                  {!canAccessTabs ? (
                    <Alert color="warning">
                      Please connect to Dentally first using the Access Token tab.
                    </Alert>
                  ) : loadingServices ? (
                    <p>Loading services...</p>
                  ) : (
                    <div>
                      <h5>Step 4: Map Services</h5>
                      <p className="text-muted">
                        Match your slot services to Dentally services for synchronization.
                      </p>
                      <div className="mapping-container mt-3">
                        <div className="row align-items-center mb-2 py-2 border-bottom gap-5">
                          <div className="col-5 ">
                            <h6 className=" mb-3">Slot Services</h6>
                          </div>

                          <div className="col-5">
                            <h6 className=" mb-3">Dentally Services</h6>
                          </div>
                        </div>
                        {slotServices.map((service) => (
                          <div
                            key={service.id}
                            className="row align-items-center mb-2 py-2 border-bottom gap-5">
                            <div className="col-5">
                              <div className="service-item">
                                <strong>{service.title}</strong>
                                <br />
                                <small className="text-muted">
                                  Duration: {service.durationInMinutes}min | Cost: £{service.cost}
                                </small>
                              </div>
                            </div>

                            <div className="col-5">
                              <Input
                                type="select"
                                size="sm"
                                value={serviceMappings[service.id] || ''}
                                onChange={(e) =>
                                  handleServiceMappingChange(service.id, e.target.value)
                                }>
                                <option value="">-- Select --</option>
                                {dentallyServices.map((dentService) => (
                                  <option key={dentService.id} value={dentService.id}>
                                    {dentService.reason}
                                  </option>
                                ))}
                              </Input>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 text-right d-flex justify-content-end">
                        <Button
                          color="primary"
                          disabled={loading}
                          onClick={handleSaveServiceMappings}>
                          {loading ? 'Saving...' : 'Save All Mappings'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </TabPane>

              <TabPane tabId="5">
                <div className="p-3">
                  {!canAccessTabs ? (
                    <Alert color="warning">
                      Please connect to Dentally first using the Access Token tab.
                    </Alert>
                  ) : loadingCustomFields ? (
                    <p>Loading custom fields...</p>
                  ) : (
                    <div>
                      <Alert color="info">
                        Our system doesn't support these fields directly. You need to either go with
                        those or you can update this.
                      </Alert>
                      <h5>Customize Fields</h5>
                      <p className="text-muted">
                        Map your patient fields with Dentally custom fields.
                      </p>
                      {customFields.length === 0 ? (
                        <p className="text-muted">No custom fields available from Dentally.</p>
                      ) : (
                        <div className="mapping-container mt-3">
                          {customFields.map((field, index) => (
                            <div
                              key={index}
                              className="row align-items-center mb-3 py-2 border-bottom">
                              <div className="col-4">
                                <Label className="mb-0">
                                  <strong>{field.label}</strong>
                                  {field.required && <span className="text-danger ml-1">*</span>}
                                  <br />
                                  <small className="text-muted">Type: {field.dataType}</small>
                                </Label>
                              </div>
                              <div className="col-1 text-center">
                                <i className="feather icon-arrow-right text-muted"></i>
                              </div>
                              <div className="col-7">
                                {field.dataType === 'select' && field.options ? (
                                  <Input
                                    type="select"
                                    size="sm"
                                    value={
                                      customFieldMappings[field.fieldName] ||
                                      field.defaultValue ||
                                      ''
                                    }
                                    onChange={(e) =>
                                      handleCustomFieldChange(field.fieldName, e.target.value)
                                    }>
                                    <option value="">-- Select Value --</option>
                                    {field.options.map((option, optIndex) => (
                                      <option key={optIndex} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </Input>
                                ) : field.dataType === 'date' ? (
                                  <Input
                                    type="date"
                                    size="sm"
                                    value={
                                      customFieldMappings[field.fieldName] ||
                                      field.defaultValue ||
                                      ''
                                    }
                                    onChange={(e) =>
                                      handleCustomFieldChange(field.fieldName, e.target.value)
                                    }
                                  />
                                ) : (
                                  <Input
                                    type="text"
                                    size="sm"
                                    value={
                                      customFieldMappings[field.fieldName] ||
                                      field.defaultValue ||
                                      ''
                                    }
                                    onChange={(e) =>
                                      handleCustomFieldChange(field.fieldName, e.target.value)
                                    }
                                  />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </TabPane>

              <TabPane tabId="6">
                <div className="p-3">
                  {!canAccessTabs ? (
                    <Alert color="warning">
                      Please connect to Dentally first using the Access Token tab.
                    </Alert>
                  ) : !canAccessPractitioners ? (
                    <Alert color="warning">
                      Please map a Site first before setting a Payment Plan.
                    </Alert>
                  ) : loadingPaymentPlans ? (
                    <p>Loading payment plans...</p>
                  ) : paymentPlans.length === 0 ? (
                    <p className="text-muted">No payment plans available.</p>
                  ) : (
                    <div>
                      <h5>Payment Plan</h5>
                      <p className="text-muted">
                        This payment plan will be used as the default when creating a patient in
                        Dentally, if we cannot find an existing patient to link to the appointment
                      </p>
                      {existingPaymentPlanId && (
                        <Alert color="success" className="mb-3">
                          Currently mapped to: <strong>{selectedPaymentPlanName}</strong>
                        </Alert>
                      )}
                      <div className="payment-plans-container mt-3">
                        {paymentPlans.map((plan) => (
                          <div
                            key={plan.id}
                            className={`payment-plan-item p-3 mb-2 border rounded ${
                              selectedPaymentPlanId === plan.id ? 'border-primary bg-light' : ''
                            }`}
                            style={{
                              cursor: 'pointer',
                              borderLeft: `4px solid ${plan.colour || '#ccc'}`
                            }}
                            onClick={() => handlePaymentPlanChange(plan)}>
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <strong>{plan.name}</strong>
                                {plan.patient_friendly_name && (
                                  <span className="text-muted ml-2">
                                    ({plan.patient_friendly_name})
                                  </span>
                                )}
                              </div>
                              {selectedPaymentPlanId === plan.id && (
                                <i className="feather icon-check-circle text-primary"></i>
                              )}
                            </div>
                            <div className="mt-2">
                              <small className="text-muted">
                                Exam: {plan.exam_duration}min | Scale & Polish:{' '}
                                {plan.scale_and_polish_duration}min | Emergency:{' '}
                                {plan.emergency_duration}min
                              </small>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 text-right d-flex justify-content-end">
                        <Button
                          color="primary"
                          disabled={!selectedPaymentPlanId || loading}
                          onClick={handleSavePaymentPlan}>
                          {loading
                            ? 'Saving...'
                            : existingPaymentPlanId
                              ? 'Update Payment Plan'
                              : 'Save Payment Plan'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </TabPane>
            </TabContent>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {activeTab === '5' && (
          <Button
            color="primary"
            disabled={loading || customFields.length === 0}
            onClick={handleSaveCustomFields}>
            {loading ? 'Saving...' : 'Save Custom Fields'}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

DentallyDetailModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  isIntegrated: PropTypes.bool,
  dentallyToken: PropTypes.string,
  refreshStatus: PropTypes.func
};

export default DentallyDetailModal;
